import React from 'react';
import {
  REVALIDATION_COLUMN_KEYS, REVALIDATION_DEFAULT_COLUMNS, REVALIDATION_COLUMN_LABELS,
  REVALIDATION_LABEL_KEYS, REVALIDATION_STATUS_COLORS, revalidationLabels,
} from './revalidationDefaults';
import ColorPicker from './ColorPicker';

/**
 * The revalidation summary's settings.
 *
 * Every colour and size the renderer reads is here, because the band ships
 * with house styling and the point of a widget is that somebody else can make
 * it theirs without editing a renderer. The defaults shown in each control are
 * the same literals renderRevalidationSummary falls back to -- if one of them
 * changes, this panel starts lying about what the report will look like.
 */
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Info'];
// The canonical report palette (SEVERITY_COLORS in report-helpers), not the
// walkthrough's darker background set: this column prints the rating as text.
const SEVERITY_TEXT = {
  Critical: '#7d1d1d', High: '#dc2626', Medium: '#f97316', Low: '#eab308', Info: '#3b82f6',
};
const STATUSES = ['Open', 'Reopened', 'Retest', 'Accepted', 'Closed'];

function RevalidationSummaryConfig({ config, update }) {
  const inp = {
    padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius)', boxSizing: 'border-box',
    border: '1px solid var(--border)', background: 'var(--bg-input)',
    color: 'var(--text)', fontFamily: 'inherit', minWidth: 0,
  };
  const lbl = { fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
  const row = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
  const group = {
    display: 'flex', flexDirection: 'column', gap: 7, padding: 8,
    borderRadius: 'var(--radius)', border: '1px solid var(--border)',
  };
  const cap = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.5px', color: 'var(--text-secondary)' };
  const note = { fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.45 };

  const num = (key, fallback, min, max, width = 52) => (
    <input type="number" min={min} max={max} value={config[key] ?? fallback}
      onChange={e => update(key, Number(e.target.value) || fallback)}
      style={{ ...inp, width }} />
  );
  // allowNone={false}: every colour here is required -- a header row has a
  // background whether or not anyone chose one.
  const colour = (key, fallback, title) => (
    <ColorPicker label={title} size={20} allowNone={false} value={config[key] || fallback}
      onChange={c => update(key, c || fallback)} />
  );
  const check = (key, label, checked, onChange) => (
    <label key={key} style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ margin: 0, cursor: 'pointer' }} />
      {label}
    </label>
  );

  const columns = Array.isArray(config.columns) && config.columns.length
    ? config.columns : [...REVALIDATION_DEFAULT_COLUMNS];
  const toggleColumn = (key) => {
    const picked = columns.includes(key) ? columns.filter(c => c !== key) : [...columns, key];
    // Sorted into the order the renderer prints in, so a column ticked later
    // lands in its proper place instead of on the end of the list.
    const next = REVALIDATION_COLUMN_KEYS.filter(k => picked.includes(k));
    // Never leave the table with no columns -- it would render a border and
    // nothing else, which reads as a broken report rather than a setting.
    update('columns', next.length ? next : [key]);
  };

  const labels = revalidationLabels(config);

  const sevFilter = Array.isArray(config.severityFilter) ? config.severityFilter : [];
  const toggleSeverity = (s) =>
    update('severityFilter', sevFilter.includes(s) ? sevFilter.filter(x => x !== s) : [...sevFilter, s]);

  const sevColours = { ...SEVERITY_TEXT, ...(config.severityColors || {}) };
  const setSevColour = (s, c) =>
    update('severityColors', { ...(config.severityColors || {}), [s]: c || SEVERITY_TEXT[s] });
  const statusColours = { ...REVALIDATION_STATUS_COLORS, ...(config.statusColors || {}) };
  const setStatusColour = (s, c) =>
    update('statusColors', { ...(config.statusColors || {}), [s]: c || REVALIDATION_STATUS_COLORS[s] });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <input value={config.heading || ''} placeholder="Heading &mdash; blank for none"
        onChange={e => update('heading', e.target.value)} style={{ ...inp, width: '100%' }} />

      <div style={group}>
        <span style={cap}>Columns</span>
        <div style={row}>
          {REVALIDATION_COLUMN_KEYS.map(k => check(
            k, REVALIDATION_COLUMN_LABELS[k], columns.includes(k), () => toggleColumn(k)))}
        </div>
        <div style={row}>
          <span style={lbl}>Headings:</span>
          {/* One box per column the table is showing, in the order it shows
              them -- a heading for a column that is not on screen is a control
              with nothing to change. */}
          {columns.map(k => (
            <input key={k} value={labels[k]}
              placeholder={REVALIDATION_COLUMN_LABELS[k]}
              aria-label={`${REVALIDATION_COLUMN_LABELS[k]} column heading`}
              onChange={e => update(REVALIDATION_LABEL_KEYS[k], e.target.value)}
              style={{ ...inp, width: 86 }} />
          ))}
        </div>
        <span style={note}>
          Untick Original Status and New Status for a plain vulnerability summary
          that still carries the original risk.
        </span>
      </div>

      <div style={group}>
        <span style={cap}>Which findings</span>
        <div style={row}>
          {check('includeOriginallyClosed', 'Include findings already closed before the retest',
            !!config.includeOriginallyClosed,
            e => update('includeOriginallyClosed', e.target.checked))}
        </div>
        <div style={row}>
          <span style={lbl}>Original risk:</span>
          {SEVERITIES.map(s => check(
            `f-${s}`, s, sevFilter.includes(s), () => toggleSeverity(s)))}
          <span style={lbl}>Max rows:</span>{num('maxRows', 0, 0, 999)}
        </div>
        <span style={note}>
          No severity ticked means every severity, and 0 rows means no limit. Only findings that
          existed when the engagement entered In Retest are listed &mdash; anything raised later was
          never retested. On an engagement that has never been in retest, every finding is listed
          with its current severity and status.
        </span>
      </div>

      <div style={group}>
        <span style={cap}>Header row</span>
        <div style={row}>
          <span style={lbl}>Background:</span>{colour('headerBg', '#24416d', 'Header background')}
          <span style={lbl}>Text:</span>{colour('headerColor', '#ffffff', 'Header text')}
          <span style={lbl}>Size:</span>{num('headerSize', 9, 5, 20)}
          {check('headerBold', 'Bold', config.headerBold !== false,
            e => update('headerBold', e.target.checked))}
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Finding column</span>
        <div style={row}>
          <span style={lbl}>Size:</span>{num('titleSize', 9, 5, 20)}
          {check('titleBold', 'Bold', !!config.titleBold, e => update('titleBold', e.target.checked))}
          <span style={lbl}>Borders:</span>{colour('borderColor', '#d8dfe8', 'Cell border')}
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Original risk</span>
        <div style={row}>
          <span style={lbl}>Style:</span>
          <select value={config.severityStyle === 'fill' ? 'fill' : 'text'}
            onChange={e => update('severityStyle', e.target.value)} style={{ ...inp, width: 110 }}>
            <option value="text">Coloured text</option>
            <option value="fill">Filled block</option>
          </select>
          <span style={lbl}>Size:</span>{num('valueSize', 8, 5, 20)}
          {check('severityUpper', 'Upper case', config.severityUpper !== false,
            e => update('severityUpper', e.target.checked))}
        </div>
        <div style={row}>
          {SEVERITIES.map(s => (
            <ColorPicker key={s} label={s} size={20} allowNone={false}
              value={sevColours[s]} onChange={c => setSevColour(s, c)} />
          ))}
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Status columns</span>
        <div style={row}>
          <span style={lbl}>Style:</span>
          <select value={config.statusStyle === 'fill' ? 'fill' : 'text'}
            onChange={e => update('statusStyle', e.target.value)} style={{ ...inp, width: 110 }}>
            <option value="text">Coloured text</option>
            <option value="fill">Filled block</option>
          </select>
          {check('statusUpper', 'Upper case', config.statusUpper !== false,
            e => update('statusUpper', e.target.checked))}
          <span style={lbl}>Text on fill:</span>{colour('valueTextColor', '#ffffff', 'Text on a filled block')}
        </div>
        <div style={row}>
          {STATUSES.map(s => (
            <ColorPicker key={s} label={s} size={20} allowNone={false}
              value={statusColours[s]} onChange={c => setStatusColour(s, c)} />
          ))}
        </div>
      </div>

      <div style={group}>
        <span style={cap}>When nothing was revalidated</span>
        <div style={row}>
          <input value={config.emptyText ?? 'No findings were revalidated.'}
            onChange={e => update('emptyText', e.target.value)}
            aria-label="Text shown when no findings match"
            style={{ ...inp, flex: 1 }} />
          {colour('emptyTextColor', '#5f6b7a', 'Empty-state text')}
        </div>
      </div>
    </div>
  );
}

export default RevalidationSummaryConfig;
